// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export const GLOSSARY: Readonly<Record<string, { title: string; body: string }>> = {
  pseudonym: {
    title: 'Pseudonym',
    body: 'A stable stand-in like EMAIL_4VCGK0189X. The same value always produces the same token under the same project key, so rows still join and the dataset still trains. It is not anonymisation: the output is still personal data.',
  },
  'project-key': {
    title: 'Project key',
    body: 'A 160-bit secret generated on this device. Pseudonyms are derived from it, so reusing the key across shards of the same dataset keeps them consistent. Anyone holding it can match candidate values against every pseudonym you produced.',
  },
  checksum: {
    title: 'Checksum',
    body: 'An arithmetic check built into a number so a typo is detectable — Luhn on cards, mod-97 on IBANs, the 3-7-1 weights on US routing numbers. It rules out a mistyped digit, not a coincidence: about one in ten random nine-digit strings passes the routing check.',
  },
  entropy: {
    title: 'Entropy',
    body: 'A measure of how unpredictable a string is. Useful in principle for spotting secrets with no recognisable prefix, and noisy in practice, because git hashes, UUIDs and base64 images all look unpredictable too. Scrubjay ships it switched off.',
  },
  'json-pointer': {
    title: 'JSON pointer',
    body: 'A path into a JSON document, RFC 6901 — /messages/2/content means the content field of the third message. findings.csv gives one per hit so you can go straight to the value.',
  },
  'aes-gcm': {
    title: 'AES-GCM-256',
    body: 'Authenticated encryption: it both hides the mapping and detects any tampering with it. Scrubjay adds a separate key commitment because AES-GCM alone does not prove which key a ciphertext belongs to.',
  },
  pbkdf2: {
    title: 'PBKDF2',
    body: 'A deliberately slow function that turns a passphrase into a key — 600,000 rounds here, the current OWASP figure. It is GPU-friendly, so it buys you time against a weak passphrase rather than safety.',
  },
  'row-group': {
    title: 'Row group',
    body: "Parquet's unit of storage and decompression. Scrubjay reads one at a time, so the honest memory ceiling on a Parquet file is the largest row group — which it prints before the scan starts.",
  },
};

/**
 * Click-to-define tooltips.
 *
 * Dismissal follows the same contract as every other overlay: Escape, a pointer anywhere
 * outside, and re-clicking the term itself. One event family only.
 */
export function initGlossary(tooltip: HTMLElement): void {
  let current: HTMLElement | null = null;

  const hide = (): void => {
    tooltip.setAttribute('hidden', '');
    current = null;
  };

  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link[data-term]');
    if (!target) {
      if (current && !tooltip.contains(e.target as Node)) hide();
      return;
    }
    const term = target.dataset['term'];
    const entry = term ? GLOSSARY[term] : undefined;
    if (!entry) return;
    if (current === target) {
      hide();
      return;
    }
    e.preventDefault();
    tooltip.replaceChildren();
    const h = document.createElement('strong');
    h.textContent = entry.title;
    tooltip.append(h, document.createTextNode(entry.body));
    tooltip.removeAttribute('hidden');
    const r = target.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
    const below = r.bottom + 8;
    tooltip.style.top =
      below + tooltip.offsetHeight > window.innerHeight ? `${Math.max(12, r.top - tooltip.offsetHeight - 8)}px` : `${below}px`;
    current = target;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) hide();
  });
  window.addEventListener('resize', hide);
}
